import { AudioClipConfig, AudioVariant, variantFile } from './AudioClipConfig';

export type DecodedClipAsset = {
  kind: 'decoded';
  /** Decoded AudioBuffer keyed by file name. One entry per unique `variant.file` (or clip default). */
  buffers: Map<string, AudioBuffer>;
};

export type StreamingClipAsset = {
  kind: 'streaming';
  /** URL keyed by file name. Streaming voices fetch one variant per playback. */
  urls: Map<string, string>;
};

export type ClipAsset = DecodedClipAsset | StreamingClipAsset;

/**
 * Runtime form of an audio clip. Owns the decoded buffers (or stream URLs) plus
 * the config; variant + pitch picking is centralized here so callers don't
 * reach inside.
 */
export class AudioClip {
  constructor(
    public readonly config: AudioClipConfig,
    public readonly asset: ClipAsset,
  ) {}

  pickVariant(): AudioVariant {
    const variants = this.config.variants;
    if (variants.length === 0) {
      throw new Error(`Audio clip "${this.config.id}" has no variants`);
    }
    if (variants.length === 1) return variants[0];
    const idx = Math.floor(Math.random() * variants.length);
    return variants[idx];
  }

  /** Random playbackRate within [min, max] from config.pitchRange. */
  pickPitch(): number {
    const [lo, hi] = this.config.pitchRange;
    if (lo === hi) return lo;
    return lo + Math.random() * (hi - lo);
  }

  /** File name a given variant should play from. */
  fileFor(variant: AudioVariant): string {
    return variantFile(variant);
  }
}
